import React, { useEffect, useMemo, useState } from "react";
import { teacherAPI } from "../services/api";
import "./StrategiesLibraryPage.css";

const CATEGORY_OPTIONS = [
  { value: "", label: "All categories" },
  { value: "sel", label: "SEL" },
  { value: "check_for_understanding", label: "Checks for Understanding" },
  { value: "discussion", label: "Discussion" },
  { value: "retrieval_practice", label: "Retrieval Practice" },
  { value: "vocabulary", label: "Vocabulary" },
  { value: "differentiation", label: "Differentiation" },
];

const STRATEGY_INSERT_DRAFT_KEY = "planning_hub_strategy_insert_draft_v1";
const STRATEGY_INSERT_PREFS_KEY = "planning_hub_strategy_insert_prefs_v1";
const INSERT_TARGET_OPTIONS = [
  { key: "selected-day", label: "Selected Planner Day" },
  { key: "specific-date", label: "Specific Date" },
];
const toDateInputKey = (value = new Date()) => {
  const copy = new Date(value);
  if (Number.isNaN(copy.getTime())) return "";
  copy.setHours(0, 0, 0, 0);
  return copy.toISOString().split("T")[0];
};

const DEFAULT_DRAFT = {
  title: "",
  category: "discussion",
  snippetText: "",
  description: "",
  referenceUrl: "",
  tagsText: "",
};

const EMPTY_LEARN_MORE_DRAFT = {
  headline: "",
  summary: "",
  sampleUsage: "",
  howToUseStepsText: "",
  lookForsText: "",
  sourceTitle: "",
  sourceUrl: "",
  sourcePages: "",
};

const categoryLabel = (value) =>
  CATEGORY_OPTIONS.find((option) => option.value === value)?.label || value || "Uncategorized";

const toTagsArray = (value) => (Array.isArray(value) ? value.map((item) => String(item || "").trim()).filter(Boolean) : []);

const StrategiesLibraryPage = ({ courseId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [draft, setDraft] = useState(DEFAULT_DRAFT);
  const [saving, setSaving] = useState(false);
  const [editingStrategyId, setEditingStrategyId] = useState(null);
  const [deletingStrategyId, setDeletingStrategyId] = useState(null);
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const [learnMoreLoading, setLearnMoreLoading] = useState(false);
  const [learnMoreError, setLearnMoreError] = useState("");
  const [learnMorePayload, setLearnMorePayload] = useState(null);
  const [learnMoreEditMode, setLearnMoreEditMode] = useState(false);
  const [learnMoreSaving, setLearnMoreSaving] = useState(false);
  const [learnMoreDraft, setLearnMoreDraft] = useState(EMPTY_LEARN_MORE_DRAFT);
  const [insertDateKey, setInsertDateKey] = useState(() => {
    if (typeof window === "undefined") return toDateInputKey();
    try {
      const raw = window.localStorage.getItem(STRATEGY_INSERT_PREFS_KEY);
      if (!raw) return toDateInputKey();
      const parsed = JSON.parse(raw);
      const saved = String(parsed?.insertDateKey || "").trim();
      return /^\d{4}-\d{2}-\d{2}$/.test(saved) ? saved : toDateInputKey();
    } catch (_error) {
      return toDateInputKey();
    }
  });
  const [insertTargetMode, setInsertTargetMode] = useState(() => {
    if (typeof window === "undefined") return "selected-day";
    try {
      const raw = window.localStorage.getItem(STRATEGY_INSERT_PREFS_KEY);
      if (!raw) return "selected-day";
      const parsed = JSON.parse(raw);
      const savedMode = String(parsed?.insertTargetMode || "").trim();
      return INSERT_TARGET_OPTIONS.some((option) => option.key === savedMode)
        ? savedMode
        : "selected-day";
    } catch (_error) {
      return "selected-day";
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const payload = {
      insertTargetMode,
      insertDateKey,
    };
    window.localStorage.setItem(STRATEGY_INSERT_PREFS_KEY, JSON.stringify(payload));
  }, [insertDateKey, insertTargetMode]);

  useEffect(() => {
    if (!learnMoreOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setLearnMoreOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [learnMoreOpen]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setErrorMessage("");
      try {
        const response = await teacherAPI.listStrategies({
          category: category || undefined,
          q: query || undefined,
        });
        if (cancelled) return;
        setRows(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        if (cancelled) return;
        setRows([]);
        setErrorMessage(
          String(error?.response?.data?.error || "Could not load strategy snippets right now."),
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [category, query]);

  const districtCount = useMemo(
    () => rows.filter((row) => row.visibility_scope === "district").length,
    [rows],
  );
  const teacherCount = useMemo(
    () => rows.filter((row) => row.visibility_scope === "teacher").length,
    [rows],
  );

  const handleCreate = async (event) => {
    event.preventDefault();
    if (!draft.title.trim() || !draft.snippetText.trim()) {
      setStatusMessage("Title and snippet text are required.");
      return;
    }

    setSaving(true);
    setStatusMessage("");
    try {
      const payload = {
        title: draft.title,
        category: draft.category,
        snippetText: draft.snippetText,
        description: draft.description,
        referenceUrl: draft.referenceUrl,
        tags: draft.tagsText
          .split(/[,\n;|]+/)
          .map((token) => token.trim())
          .filter(Boolean),
      };

      if (editingStrategyId) {
        await teacherAPI.updateStrategy(editingStrategyId, payload);
      } else {
        await teacherAPI.createStrategy(payload);
      }

      setDraft(DEFAULT_DRAFT);
      setEditingStrategyId(null);
      setStatusMessage(
        editingStrategyId ? "Strategy updated." : "Strategy saved to My Strategies.",
      );

      const response = await teacherAPI.listStrategies({
        category: category || undefined,
        q: query || undefined,
      });
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setStatusMessage(String(error?.response?.data?.error || "Could not save strategy."));
    } finally {
      setSaving(false);
    }
  };

  const beginEdit = (row) => {
    setEditingStrategyId(row.id);
    setDraft({
      title: String(row.title || ""),
      category: String(row.category || "discussion"),
      snippetText: String(row.snippet_text || ""),
      description: String(row.description || ""),
      referenceUrl: String(row.reference_url || ""),
      tagsText: toTagsArray(row.tags).join(", "),
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingStrategyId(null);
    setDraft(DEFAULT_DRAFT);
    setStatusMessage("Edit canceled.");
  };

  const handleDelete = async (row) => {
    const ok = window.confirm(`Delete "${row.title}" from My Strategies?`);
    if (!ok) return;

    setDeletingStrategyId(row.id);
    setStatusMessage("");
    try {
      await teacherAPI.deleteStrategy(row.id);
      setRows((prev) => prev.filter((item) => item.id !== row.id));
      if (editingStrategyId === row.id) {
        setEditingStrategyId(null);
        setDraft(DEFAULT_DRAFT);
      }
      setStatusMessage("Strategy deleted.");
    } catch (error) {
      setStatusMessage(String(error?.response?.data?.error || "Could not delete strategy."));
    } finally {
      setDeletingStrategyId(null);
    }
  };

  const openLearnMore = async (row) => {
    setLearnMoreOpen(true);
    setLearnMoreLoading(true);
    setLearnMoreError("");
    setLearnMorePayload(null);
    setLearnMoreEditMode(false);
    setLearnMoreDraft(EMPTY_LEARN_MORE_DRAFT);
    try {
      const response = await teacherAPI.getStrategyLearnMore(row.id);
      setLearnMorePayload(response.data || null);
    } catch (error) {
      setLearnMoreError(String(error?.response?.data?.error || "Could not load guidance."));
    } finally {
      setLearnMoreLoading(false);
    }
  };

  const closeLearnMore = () => {
    setLearnMoreOpen(false);
    setLearnMoreLoading(false);
    setLearnMoreError("");
    setLearnMoreEditMode(false);
    setLearnMoreSaving(false);
    setLearnMoreDraft(EMPTY_LEARN_MORE_DRAFT);
  };

  const beginLearnMoreEdit = () => {
    const learnMore = learnMorePayload?.learnMore || {};
    const toLineText = (value) =>
      Array.isArray(value)
        ? value.map((item) => String(item || "").trim()).filter(Boolean).join("\n")
        : "";

    setLearnMoreDraft({
      headline: String(learnMore.headline || "").trim(),
      summary: String(learnMore.summary || "").trim(),
      sampleUsage: String(learnMore.sample_usage || "").trim(),
      howToUseStepsText: toLineText(learnMore.how_to_use_steps),
      lookForsText: toLineText(learnMore.look_fors),
      sourceTitle: String(learnMore.source_title || "").trim(),
      sourceUrl: String(learnMore.source_url || "").trim(),
      sourcePages: String(learnMore.source_pages || "").trim(),
    });
    setLearnMoreEditMode(true);
    setLearnMoreError("");
  };

  const cancelLearnMoreEdit = () => {
    setLearnMoreEditMode(false);
    setLearnMoreSaving(false);
    setLearnMoreError("");
  };

  const saveLearnMoreEdit = async () => {
    const strategyId = learnMorePayload?.strategy?.id;
    if (!strategyId) {
      setLearnMoreError("Could not determine strategy id for save.");
      return;
    }

    const parseLines = (value) =>
      String(value || "")
        .split(/\n+/)
        .map((line) => line.replace(/^[\s\u2022*-]+/, "").trim())
        .filter(Boolean);

    if (!learnMoreDraft.headline.trim() || !learnMoreDraft.summary.trim()) {
      setLearnMoreError("Headline and summary are required.");
      return;
    }

    setLearnMoreSaving(true);
    setLearnMoreError("");
    try {
      const response = await teacherAPI.updateStrategyLearnMore(strategyId, {
        headline: learnMoreDraft.headline,
        summary: learnMoreDraft.summary,
        sampleUsage: learnMoreDraft.sampleUsage,
        howToUseSteps: parseLines(learnMoreDraft.howToUseStepsText),
        lookFors: parseLines(learnMoreDraft.lookForsText),
        sourceTitle: learnMoreDraft.sourceTitle,
        sourceUrl: learnMoreDraft.sourceUrl,
        sourcePages: learnMoreDraft.sourcePages,
      });
      setLearnMorePayload(response.data || null);
      setLearnMoreEditMode(false);
      setStatusMessage("Strategy guidance updated.");
    } catch (error) {
      setLearnMoreError(String(error?.response?.data?.error || "Could not save guidance."));
    } finally {
      setLearnMoreSaving(false);
    }
  };

  const pushSnippetToPlanner = (targetField, row) => {
    const snippetText = String(row?.snippet_text || "").trim();
    if (!snippetText) {
      setStatusMessage("This strategy does not have snippet text yet.");
      return;
    }

    const targetDateKey =
      insertTargetMode === "specific-date" ? String(insertDateKey || "").trim() : "";
    if (insertTargetMode === "specific-date" && !/^\d{4}-\d{2}-\d{2}$/.test(targetDateKey)) {
      setStatusMessage("Pick a valid target date before inserting.");
      return;
    }

    const payload = {
      targetField,
      snippetText,
      strategyId: row.id,
      courseId: courseId || null,
      targetDateKey: targetDateKey || null,
      createdAt: new Date().toISOString(),
    };

    window.sessionStorage.setItem(STRATEGY_INSERT_DRAFT_KEY, JSON.stringify(payload));
    setStatusMessage(
      targetField === "objective"
        ? `Snippet queued for objective on ${targetDateKey || "selected planner day"}.`
        : `Snippet queued for notes on ${targetDateKey || "selected planner day"}.`,
    );
    window.location.hash = "/planning/week";
  };

  const canEditLearnMore = Boolean(learnMorePayload?.strategy?.can_edit_learn_more);

  return (
    <div className="panel-card strategies-library-page">
      <div className="strategies-header">
        <div>
          <h2>Strategy Library</h2>
          <p>Insert district strategies fast, and build your own reusable snippets.</p>
        </div>
        <div className="strategies-kpis">
          <span><strong>{rows.length}</strong> visible</span>
          <span><strong>{districtCount}</strong> district</span>
          <span><strong>{teacherCount}</strong> mine</span>
        </div>
      </div>

      <div className="strategies-filters">
        <input
          type="search"
          placeholder="Search strategies"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search strategies"
        />
        <select
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          aria-label="Filter by category"
        >
          {CATEGORY_OPTIONS.map((option) => (
            <option key={option.value || "all"} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="strategy-insert-target">
          <span className="strategy-insert-target-label">Insert target</span>
          <div className="strategy-insert-target-toggle" role="group" aria-label="Insert target">
            {INSERT_TARGET_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`strategy-target-btn ${
                  insertTargetMode === option.key ? "active" : ""
                }`}
                onClick={() => setInsertTargetMode(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>
          {insertTargetMode === "specific-date" ? (
            <input
              id="strategy-insert-date"
              type="date"
              value={insertDateKey}
              onChange={(event) => setInsertDateKey(event.target.value)}
              aria-label="Target date for planner insertion"
            />
          ) : (
            <p className="strategy-insert-target-hint">
              Uses the currently selected day in Weekly Planner.
            </p>
          )}
        </div>
      </div>

      <form className="strategy-create-form" onSubmit={handleCreate}>
        <h3>{editingStrategyId ? "Edit My Strategy" : "Add to My Strategies"}</h3>
        <div className="strategy-create-grid">
          <input
            type="text"
            placeholder="Strategy title"
            value={draft.title}
            onChange={(event) => setDraft((prev) => ({ ...prev, title: event.target.value }))}
          />
          <select
            value={draft.category}
            onChange={(event) => setDraft((prev) => ({ ...prev, category: event.target.value }))}
          >
            {CATEGORY_OPTIONS.filter((option) => option.value).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            type="url"
            placeholder="Reference URL (optional)"
            value={draft.referenceUrl}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, referenceUrl: event.target.value }))
            }
          />
          <input
            type="text"
            placeholder="Tags (comma separated)"
            value={draft.tagsText}
            onChange={(event) => setDraft((prev) => ({ ...prev, tagsText: event.target.value }))}
          />
          <textarea
            rows={2}
            placeholder="Short description (optional)"
            value={draft.description}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, description: event.target.value }))
            }
          />
          <textarea
            rows={3}
            placeholder="Snippet text to paste into lesson plans"
            value={draft.snippetText}
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, snippetText: event.target.value }))
            }
          />
        </div>
        <div className="strategy-form-actions">
          <button type="submit" className="primary-btn" disabled={saving}>
            {saving ? "Saving..." : editingStrategyId ? "Update Strategy" : "Save Strategy"}
          </button>
          {editingStrategyId ? (
            <button type="button" className="nav-lite-btn" onClick={cancelEdit}>
              Cancel Edit
            </button>
          ) : null}
        </div>
        {statusMessage ? <p className="strategies-status">{statusMessage}</p> : null}
      </form>

      {loading && <p>Loading strategies...</p>}
      {!loading && errorMessage ? <p className="strategies-error">{errorMessage}</p> : null}

      {!loading && !errorMessage && rows.length === 0 ? (
        <p>No strategies matched this filter.</p>
      ) : null}

      {!loading && !errorMessage && rows.length > 0 && (
        <div className="strategies-list">
          {rows.map((row) => (
            <article key={row.id} className="strategy-card">
              <div className="strategy-card-header">
                <h4>{row.title}</h4>
                <div className="strategy-meta">
                  <span>{categoryLabel(row.category)}</span>
                  <span>{row.visibility_scope === "teacher" ? "My strategy" : "District"}</span>
                </div>
              </div>
              {row.description ? <p>{row.description}</p> : null}
              <pre>{row.snippet_text}</pre>
              {Array.isArray(row.tags) && row.tags.length > 0 ? (
                <div className="strategy-tags">
                  {row.tags.map((tag) => (
                    <span key={`${row.id}-${tag}`}>{tag}</span>
                  ))}
                </div>
              ) : null}
              <div className="strategy-card-links">
                <button
                  type="button"
                  className="nav-lite-btn"
                  onClick={() => openLearnMore(row)}
                >
                  Learn how to use
                </button>
                {row.reference_url ? (
                  <a href={row.reference_url} target="_blank" rel="noreferrer noopener">
                    Source
                  </a>
                ) : null}
              </div>
              <div className="strategy-card-actions">
                <button
                  type="button"
                  className="nav-lite-btn"
                  onClick={() => pushSnippetToPlanner("objective", row)}
                >
                  Insert in Objective
                </button>
                <button
                  type="button"
                  className="nav-lite-btn"
                  onClick={() => pushSnippetToPlanner("notes", row)}
                >
                  Insert in Notes
                </button>
                {row.visibility_scope === "teacher" ? (
                  <>
                    <button
                      type="button"
                      className="nav-lite-btn"
                      onClick={() => beginEdit(row)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="nav-lite-btn"
                      onClick={() => handleDelete(row)}
                      disabled={deletingStrategyId === row.id}
                    >
                      {deletingStrategyId === row.id ? "Deleting..." : "Delete"}
                    </button>
                  </>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      )}

      {learnMoreOpen ? (
        <div
          className="strategy-learn-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Strategy implementation guidance"
          onClick={closeLearnMore}
        >
          <div className="strategy-learn-modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="strategy-learn-modal-header">
              <h3>{learnMorePayload?.learnMore?.headline || "How to use this strategy"}</h3>
              <div className="strategy-learn-modal-actions">
                {canEditLearnMore && !learnMoreEditMode ? (
                  <button type="button" className="nav-lite-btn" onClick={beginLearnMoreEdit}>
                    Edit guidance
                  </button>
                ) : null}
                {canEditLearnMore && learnMoreEditMode ? (
                  <>
                    <button
                      type="button"
                      className="primary-btn"
                      onClick={saveLearnMoreEdit}
                      disabled={learnMoreSaving}
                    >
                      {learnMoreSaving ? "Saving..." : "Save"}
                    </button>
                    <button type="button" className="nav-lite-btn" onClick={cancelLearnMoreEdit}>
                      Cancel
                    </button>
                  </>
                ) : null}
                <button type="button" className="nav-lite-btn" onClick={closeLearnMore}>
                  Close
                </button>
              </div>
            </div>

            {learnMoreLoading ? <p>Loading guidance...</p> : null}
            {!learnMoreLoading && learnMoreError ? (
              <p className="strategies-error">{learnMoreError}</p>
            ) : null}

            {!learnMoreLoading && !learnMoreError && learnMorePayload && !learnMoreEditMode ? (
              <div className="strategy-learn-modal-body">
                {learnMorePayload?.strategy?.title ? (
                  <p className="strategy-learn-strategy-title">
                    Strategy: <strong>{learnMorePayload.strategy.title}</strong>
                  </p>
                ) : null}
                {learnMorePayload?.learnMore?.summary ? (
                  <p>{learnMorePayload.learnMore.summary}</p>
                ) : null}
                {learnMorePayload?.learnMore?.sample_usage ? (
                  <>
                    <h4>Sample in action</h4>
                    <p>{learnMorePayload.learnMore.sample_usage}</p>
                  </>
                ) : null}

                {Array.isArray(learnMorePayload?.learnMore?.how_to_use_steps)
                && learnMorePayload.learnMore.how_to_use_steps.length > 0 ? (
                  <>
                    <h4>How to use it</h4>
                    <ol>
                      {learnMorePayload.learnMore.how_to_use_steps.map((step, index) => (
                        <li key={`how-${index}`}>{step}</li>
                      ))}
                    </ol>
                  </>
                  ) : null}

                {Array.isArray(learnMorePayload?.learnMore?.look_fors)
                && learnMorePayload.learnMore.look_fors.length > 0 ? (
                  <>
                    <h4>Look-fors</h4>
                    <ul>
                      {learnMorePayload.learnMore.look_fors.map((item, index) => (
                        <li key={`look-${index}`}>{item}</li>
                      ))}
                    </ul>
                  </>
                  ) : null}

                {learnMorePayload?.learnMore?.source_url ? (
                  <div className="strategy-learn-source">
                    <strong>{learnMorePayload.learnMore.source_title || "Source"}</strong>
                    {learnMorePayload.learnMore.source_pages ? (
                      <span>{learnMorePayload.learnMore.source_pages}</span>
                    ) : null}
                    <a
                      href={learnMorePayload.learnMore.source_url}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Open source
                    </a>
                  </div>
                ) : null}
              </div>
            ) : null}

            {!learnMoreLoading && !learnMoreError && learnMorePayload && learnMoreEditMode ? (
              <div className="strategy-learn-modal-body strategy-learn-edit-grid">
                <label>
                  Headline
                  <input
                    type="text"
                    value={learnMoreDraft.headline}
                    onChange={(event) =>
                      setLearnMoreDraft((prev) => ({ ...prev, headline: event.target.value }))
                    }
                  />
                </label>
                <label>
                  Summary
                  <textarea
                    rows={3}
                    value={learnMoreDraft.summary}
                    onChange={(event) =>
                      setLearnMoreDraft((prev) => ({ ...prev, summary: event.target.value }))
                    }
                  />
                </label>
                <label>
                  Sample in action
                  <textarea
                    rows={3}
                    value={learnMoreDraft.sampleUsage}
                    onChange={(event) =>
                      setLearnMoreDraft((prev) => ({ ...prev, sampleUsage: event.target.value }))
                    }
                  />
                </label>
                <label>
                  How to use steps (one per line)
                  <textarea
                    rows={5}
                    value={learnMoreDraft.howToUseStepsText}
                    onChange={(event) =>
                      setLearnMoreDraft((prev) => ({
                        ...prev,
                        howToUseStepsText: event.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  Look-fors (one per line)
                  <textarea
                    rows={4}
                    value={learnMoreDraft.lookForsText}
                    onChange={(event) =>
                      setLearnMoreDraft((prev) => ({ ...prev, lookForsText: event.target.value }))
                    }
                  />
                </label>
                <label>
                  Source title
                  <input
                    type="text"
                    value={learnMoreDraft.sourceTitle}
                    onChange={(event) =>
                      setLearnMoreDraft((prev) => ({ ...prev, sourceTitle: event.target.value }))
                    }
                  />
                </label>
                <label>
                  Source URL
                  <input
                    type="url"
                    value={learnMoreDraft.sourceUrl}
                    onChange={(event) =>
                      setLearnMoreDraft((prev) => ({ ...prev, sourceUrl: event.target.value }))
                    }
                  />
                </label>
                <label>
                  Source pages
                  <input
                    type="text"
                    value={learnMoreDraft.sourcePages}
                    onChange={(event) =>
                      setLearnMoreDraft((prev) => ({ ...prev, sourcePages: event.target.value }))
                    }
                  />
                </label>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default StrategiesLibraryPage;
